import { STATIC_SITE_DEFAULTS } from '../content/staticDefaults';
import {
  ensurePostsHaveTopicKeys,
  normalizeBlogCategories,
  syncBrowseTopicKeys,
} from '../content/blogCategories';
import {
  ensureCardsHaveCategoryKeys,
  normalizeCaseStudyCategories,
} from '../content/caseStudyCategories';
import { normalizeCaseStudyDetail } from '../content/caseStudyDetailTemplate';
import { resolveBlogHeroVideoUrl } from '../media/blogHeroMedia';
import { normalizeServicePage } from '../content/servicePageTemplate';
import { normalizeAboutPage } from '../content/aboutPageTemplate';
import { normalizeHomePage } from '../content/homePageTemplate';
import { normalizeServicesPage } from '../content/servicesPageTemplate';
import { normalizeCaseStudiesPage } from '../content/caseStudiesPageTemplate';
import { normalizeContactPage } from '../content/contactPageTemplate';

/** Merge API-stored content with bundled defaults (empty API → full defaults). */
export function mergeWithDefaults(apiContent = {}) {
  const defaults = STATIC_SITE_DEFAULTS;
  const api = apiContent || {};

  return {
    services: {
      list: Array.isArray(api.services?.list) ? api.services.list : defaults.services.list,
      heroSlides: Array.isArray(api.services?.heroSlides)
        ? api.services.heroSlides
        : defaults.services.heroSlides,
      details: {
        ...defaults.services.details,
        ...(api.services?.details && typeof api.services.details === 'object'
          ? api.services.details
          : {}),
      },
    },
    caseStudies: (() => {
      const categories = normalizeCaseStudyCategories(
        api.caseStudies?.categories?.length
          ? api.caseStudies.categories
          : defaults.caseStudies.categories
      );
      const cards = ensureCardsHaveCategoryKeys(
        Array.isArray(api.caseStudies?.cards) ? api.caseStudies.cards : defaults.caseStudies.cards,
        categories
      );
      const mergedDetails = {
        ...defaults.caseStudies.details,
        ...(api.caseStudies?.details && typeof api.caseStudies.details === 'object'
          ? api.caseStudies.details
          : {}),
      };
      const details = Object.fromEntries(
        Object.entries(mergedDetails).map(([slug, detail]) => [
          slug,
          normalizeCaseStudyDetail(detail, slug),
        ])
      );
      return {
        stats: api.caseStudies?.stats ?? defaults.caseStudies.stats,
        categories,
        cards,
        details,
      };
    })(),
    blogs: (() => {
      const categories = normalizeBlogCategories(
        api.blogs?.categories?.length ? api.blogs.categories : defaults.blogs.categories
      );
      const featuredIdsByTopic =
        api.blogs?.featuredIdsByTopic ?? defaults.blogs.featuredIdsByTopic;
      const posts = ensurePostsHaveTopicKeys(
        Array.isArray(api.blogs?.posts) ? api.blogs.posts : defaults.blogs.posts,
        categories,
        featuredIdsByTopic
      );
      return {
        posts,
        categories,
        videoPool:
          api.blogs?.videoPool ??
          api.media?.blogVideoPool ??
          defaults.blogs.videoPool,
        heroVideo: resolveBlogHeroVideoUrl(
          api.blogs?.heroVideo,
          api.media?.blogHeroVideo,
          defaults.blogs.heroVideo
        ),
        featuredIdsByTopic,
        browseTopicKeys: syncBrowseTopicKeys(categories),
      };
    })(),
    team: api.team ?? defaults.team,
    testimonials: {
      items: Array.isArray(api.testimonials?.items)
        ? api.testimonials.items
        : defaults.testimonials.items,
    },
    media: {
      blogHeroVideo: resolveBlogHeroVideoUrl(
        api.media?.blogHeroVideo,
        api.blogs?.heroVideo,
        defaults.media.blogHeroVideo
      ),
      blogVideoPool:
        api.media?.blogVideoPool ??
        api.blogs?.videoPool ??
        defaults.media.blogVideoPool,
      solutionVideos: api.media?.solutionVideos ?? defaults.media.solutionVideos,
    },
    servicePages: (() => {
      const list = Array.isArray(api.servicePages?.list) ? api.servicePages.list : defaults.servicePages.list;
      const rawPages =
        api.servicePages?.pages && typeof api.servicePages.pages === 'object'
          ? api.servicePages.pages
          : defaults.servicePages.pages;
      const pages = Object.fromEntries(
        Object.entries(rawPages).map(([slug, doc]) => {
          const listEntry = list.find((s) => s.slug === slug);
          return [slug, normalizeServicePage(doc, slug, listEntry?.title || doc?.title)];
        })
      );
      return { list, pages };
    })(),
    aboutPage: normalizeAboutPage(api.aboutPage || defaults.aboutPage),
    homePage: normalizeHomePage(api.homePage || defaults.homePage),
    servicesPage: normalizeServicesPage(api.servicesPage || defaults.servicesPage),
    caseStudiesPage: normalizeCaseStudiesPage(api.caseStudiesPage || defaults.caseStudiesPage),
    contactPage: normalizeContactPage(api.contactPage || defaults.contactPage),
    updatedAt: api.updatedAt || null,
  };
}

/** Always merge + normalize so published CMS data gets categories, categoryKey, and safe detail shapes. */
export function resolveSiteContent(apiRaw) {
  return mergeWithDefaults(apiRaw || {});
}
